import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { COPY_FAILED, COPY_REJECTION, DELETE_FAILED, DELETE_REJECTION, DuplicateCompanyName, RecruitApi, SMART_JD_GONE, SmartJdLocked, SmartJdLockRequired, WRITE_REJECTION } from './recruit-api';
import { Company, CompanyPage, NewCompany } from '../models/company.model';
import { Job, JobPage, NewJobRef, RecruitEvent } from '../models/job.model';
import { NewResume, ResumeUpload } from '../models/resume.model';
import { SmartJd, SmartJdDocument, SmartJdLock } from '../models/smart-jd.model';
import { JobMatches } from '../models/match-result.model';
import { AuthService } from '../auth/auth.service';

/** Recruit Service names its own reasons in a `slug`. Unknown slugs are shown as themselves. */
const UPLOAD_REJECTION: Record<string, string> = {
  'job-not-found': 'No job with this short code.',
  'smart-jd-not-found': 'That job has no published Smart JD, so resumes cannot be scored against it.',
  'unsupported-file-type': 'Recruit Service does not accept that file type.',
};

/** Recruit Service names its own reasons in a `slug`; anything else is a transport failure. */
function slugOf(e: unknown): string | undefined {
  return e instanceof HttpErrorResponse ? (e.error as { slug?: string } | null)?.slug : undefined;
}

function uploadError(e: unknown): string {
  const slug = slugOf(e);
  if (slug) return UPLOAD_REJECTION[slug] ?? slug;
  return 'The upload did not go through.';
}

/** `smart-jd-locked` is typed rather than mapped: it carries who holds the lock and until when. */
function writeError(e: unknown, fallback: string): Error {
  if (!(e instanceof HttpErrorResponse)) return e instanceof Error ? e : new Error(fallback);
  const body = e.error as { slug?: string; lockedBy?: string; lockExpires?: string } | null;
  if (body?.slug === 'smart-jd-locked' && body.lockedBy && body.lockExpires) {
    return new SmartJdLocked({ lockedBy: body.lockedBy, lockExpires: body.lockExpires });
  }
  if (e.status === 404) return new Error(SMART_JD_GONE);
  const slug = slugOf(e);
  if (slug === 'smart-jd-lock-required') return new SmartJdLockRequired();
  return new Error(slug ? (WRITE_REJECTION[slug] ?? slug) : fallback);
}

/** A bodyless refusal is still the only one the contract defines for that status. */
function copyError(e: unknown): Error {
  if (!(e instanceof HttpErrorResponse)) return e instanceof Error ? e : new Error(COPY_FAILED);
  // A server error is never the domain speaking, whatever it happens to carry.
  if (e.status >= 500) return new Error(COPY_FAILED);
  const slug = slugOf(e);
  if (slug) return new Error(COPY_REJECTION[slug] ?? slug);
  if (e.status === 409) return new Error(COPY_REJECTION['smart-jd-not-published']);
  if (e.status === 404) return new Error(COPY_REJECTION['job-not-found']);
  return new Error(COPY_FAILED);
}

/** The contract defines one refusal for a delete, and a bodyless 404 is how it usually arrives. */
function deleteError(e: unknown): Error {
  if (!(e instanceof HttpErrorResponse)) return e instanceof Error ? e : new Error(DELETE_FAILED);
  if (e.status >= 500) return new Error(DELETE_FAILED);
  const slug = slugOf(e);
  if (slug) return new Error(DELETE_REJECTION[slug] ?? slug);
  if (e.status === 404) return new Error(DELETE_REJECTION['job-not-found']);
  return new Error(DELETE_FAILED);
}

/** Recruit Service over HTTP. Every method here now has a real endpoint behind it. */
const PAGE_SIZE = 20;

/** The event names the stream carries. One listener each: an unlisted name is never delivered. */
const BUILD_EVENT = 'smart-jd-built';
const MATCH_EVENT = 'match-built';


@Injectable()
export class HttpRecruitApi extends RecruitApi {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly base = environment.apiBaseUrl;

  override listCompanies(page: number): Observable<CompanyPage> {
    return this.http.get<CompanyPage>(this.base + '/companies', { params: { page, limit: PAGE_SIZE } });
  }

  override getCompany(id: string): Observable<Company> {
    return this.http.get<Company>(this.base + '/companies/' + id);
  }

  override createCompany(input: NewCompany): Observable<Company> {
    return this.http.post<Company>(this.base + '/companies', input).pipe(
      catchError((e: unknown) => throwError(() => (e instanceof HttpErrorResponse && e.status === 409 ? new DuplicateCompanyName() : e))),
    );
  }

  override listJobs(companyId: string, page: number): Observable<JobPage> {
    return this.http.get<JobPage>(this.base + '/companies/' + companyId + '/jobs', { params: { page, limit: PAGE_SIZE } });
  }

  override uploadJd(companyId: string, file: File): Observable<NewJobRef> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<NewJobRef>(this.base + '/companies/' + companyId + '/jobs', body);
  }

  override copyJob(jobShortCode: string): Observable<Job> {
    return this.http
      .post<Job>(this.base + '/jobs/' + jobShortCode + '/copy', null)
      .pipe(catchError((e: unknown) => throwError(() => copyError(e))));
  }

  override deleteJob(jobShortCode: string): Observable<void> {
    return this.http
      .delete<void>(this.base + '/jobs/' + jobShortCode)
      .pipe(catchError((e: unknown) => throwError(() => deleteError(e))));
  }

  override getSmartJd(jobShortCode: string): Observable<SmartJdDocument | null> {
    return this.http.get<SmartJdDocument>(this.smartJdUrl(jobShortCode)).pipe(
      catchError((e: unknown) => {
        if (e instanceof HttpErrorResponse && e.status === 404) return of(null);
        return throwError(() => e);
      }),
    );
  }


  override uploadResumes(jobShortCode: string, resumes: NewResume[]): Observable<ResumeUpload[]> {
    const url = this.base + '/jobs/' + jobShortCode + '/resumes';
    // Each upload reports its own outcome: uploads are never de-duplicated, so a caller told
    // only "the batch failed" would re-send the ones that worked and duplicate those candidates.
    const posts = resumes.map((r) => {
      const body = new FormData();
      body.append('file', r.file, r.file.name);
      body.append('name', r.name);
      return this.http.post(url, body).pipe(
        map((): ResumeUpload => ({ fileName: r.file.name, name: r.name, uploaded: true, error: null })),
        catchError((e: unknown) => of<ResumeUpload>({ fileName: r.file.name, name: r.name, uploaded: false, error: uploadError(e) })),
      );
    });
    return posts.length ? forkJoin(posts) : of([]);
  }


  override listMatches(jobShortCode: string): Observable<JobMatches> {
    return this.http.get<JobMatches>(this.base + '/jobs/' + jobShortCode + '/matches');
  }

  override lockSmartJd(jobShortCode: string): Observable<SmartJdLock> {
    return this.http.post<SmartJdLock>(this.lockUrl(jobShortCode), null).pipe(
      catchError((e: unknown) => throwError(() => writeError(e, 'The edit lock could not be taken.'))),
    );
  }

  override saveSmartJd(jobShortCode: string, smartJd: SmartJd): Observable<SmartJdDocument> {
    return this.http.put<SmartJdDocument>(this.smartJdUrl(jobShortCode), { smartJd }).pipe(
      catchError((e: unknown) => throwError(() => writeError(e, 'The change was not saved.'))),
    );
  }

  override publishSmartJd(jobShortCode: string): Observable<SmartJdDocument> {
    return this.http.post<SmartJdDocument>(this.base + '/jobs/' + jobShortCode + '/smart-jd/publish', null).pipe(
      catchError((e: unknown) => throwError(() => writeError(e, 'Publishing did not go through.'))),
    );
  }

  override releaseSmartJdLock(jobShortCode: string): Observable<void> {
    return this.http.delete<void>(this.lockUrl(jobShortCode)).pipe(
      catchError((e: unknown) => throwError(() => writeError(e, 'The edit lock could not be released.'))),
    );
  }

  /** The token rides in the query string because EventSource cannot set a header — so the
   *  interceptor is no help here. Recruit Service reads it after the header and the cookie. */
  override events(): Observable<RecruitEvent> {
    return new Observable<RecruitEvent>((subscriber) => {
      const token = this.auth.token();
      const source = new EventSource(this.base + '/events' + (token ? '?jwt=' + encodeURIComponent(token) : ''));
      // One connection, a listener per event name, each framing its payload with the name it
      // arrived under. A frame Vetta cannot read is a stream problem, and the safety poll covers it.
      const listen = (name: RecruitEvent['name']) =>
        source.addEventListener(name, (e) => {
          try {
            subscriber.next({ name, data: JSON.parse((e as MessageEvent<string>).data) } as RecruitEvent);
          } catch {
            // Deliberately silent: see above.
          }
        });
      listen(BUILD_EVENT);
      listen(MATCH_EVENT);
      source.onerror = () => subscriber.error(new Error('The event stream closed.'));
      return () => source.close();
    });
  }

  private smartJdUrl(jobShortCode: string): string {
    return this.base + '/jobs/' + jobShortCode + '/smart-jd';
  }

  private lockUrl(jobShortCode: string): string {
    return this.smartJdUrl(jobShortCode) + '/lock';
  }
}
